import React, { useState, useEffect } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useMediaQuery } from 'react-responsive';
import Constant from "../Constant";
import { getAccessToken } from "../../hooks/AccessToken";
import Axios from "axios";

const EditWinner = ({
  open,
  onClose,
  contestId,
  winners,
  onWinnersUpdated,
  showNotification,
}) => {
  const [winnersList, setWinnersList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedWinnerIndex, setSelectedWinnerIndex] = useState(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Add mobile responsiveness
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    if (open) {
      fetchWinnersDetails();
    }
  }, [open, winners]);

  const fetchWinnersDetails = async () => {
    setLoading(true);
    try {
      const token = getAccessToken();

      // Fetch all participants to get winner details
      const response = await Axios.get(
        `https://api.storytent.in/api/openMic/getParticipantsById/${contestId}`,
        {
          headers: {
            token: token,
          },
        }
      );
      const result = response;

      if (result.status === 200 && result.data) {
        const participants = result.data.data;
        // Map the winners array to include full details from participants
        const winnersWithDetails = [];

        if (winners && winners.length > 0) {
          for (let i = 0; i < winners.length; i++) {
            const winnerParticipant = participants.find(
              (p) => p._id === winners[i]
            );

            if (winnerParticipant) {
              winnersWithDetails.push({
                id: winnerParticipant._id,
                name: winnerParticipant.name,
                position: i + 1,
              });
            } else {
              // In case the winner ID doesn't match any participant
              winnersWithDetails.push({
                id: winners[i],
                name: "Unknown Participant",
                position: i + 1,
              });
            }
          }
        }

        setWinnersList(winnersWithDetails);
      } else {
        showNotification("Failed to load participants data", "error");
      }
    } catch (error) {
      showNotification("Failed to load winners details", "error");
      console.error("Error loading winners details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    try {
      const token = getAccessToken();
      // Fetch all participants
      const response = await Axios.get(
        `https://api.storytent.in/api/openMic/getParticipantsById/${contestId}`,
        {
          headers: {
            token: token,
          },
        }
      );
      const result = response;

      if (result.status === 200 && result.data) {
        // Filter participants based on search term
        const filteredResults = result.data.data.filter((participant) =>
          participant.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Map to the format needed for search results
        const formattedResults = filteredResults.map((participant) => ({
          id: participant._id,
          name: participant.name,
        }));

        setSearchResults(formattedResults);
      } else {
        showNotification("Failed to search participants", "error");
      }
    } catch (error) {
      showNotification("Failed to search users", "error");
      console.error("Error searching users:", error);
    }
  };

  const selectWinner = (userId, userName) => {
    if (selectedWinnerIndex !== null) {
      const updatedWinnersList = [...winnersList];
      updatedWinnersList[selectedWinnerIndex] = {
        ...updatedWinnersList[selectedWinnerIndex],
        id: userId,
        name: userName,
      };
      setWinnersList(updatedWinnersList);
      setSelectedWinnerIndex(null);
      setIsSearchActive(false);
      setSearchTerm("");
      setSearchResults([]);
    }
  };

  const handleEditClick = (index) => {
    setSelectedWinnerIndex(index);
    setIsSearchActive(true);
    // Focus on search input
    setTimeout(() => {
      const searchInput = document.getElementById("winner-search-input");
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Extract winner IDs in the correct order
      const updatedWinnerIds = winnersList.map((winner) => winner.id);

      const response = await Axios.patch(
        `https://api.storytent.in/api/openMic/editWinners/${contestId}`,
        {
          winnersId: updatedWinnerIds,
        }
      );

      console.log("Updating winners with:", response.data);
      if (response.status === 200) {
        showNotification("Winners updated successfully");
        onWinnersUpdated(updatedWinnerIds);
      }
      onClose();
    } catch (error) {
      if (error.response && error.response.data) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to update winners", "error");
      }
      console.error("Error updating winners:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveWinner = (indexToRemove) => {
    console.log(indexToRemove);
    console.log(winnersList.length);
    setWinnersList(prev =>
      prev.filter((_, idx) => idx !== indexToRemove)
    );
  };

  const modalStyle = {
    modalContent: {
      padding: isMobile ? "15px" : "20px",
      maxWidth: "600px",
      maxHeight: "80vh",
      overflow: "auto",
      fontSize: isMobile ? "14px" : "16px",
    },
    searchContainer: {
      marginBottom: "20px",
    },
    searchForm: {
      display: "flex",
      marginBottom: "10px",
    },
    searchInput: {
      flex: 1,
      padding: isMobile ? "6px" : "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      marginRight: isMobile ? "5px" : "10px",
      fontSize: isMobile ? "14px" : "16px",
    },
    searchButton: {
      padding: isMobile ? "6px 12px" : "8px 16px",
      borderRadius: "4px",
      backgroundColor: "#4a90e2",
      color: "white",
      border: "none",
      cursor: "pointer",
      fontSize: isMobile ? "14px" : "16px",
    },
    searchResults: {
      border: "1px solid #ddd",
      borderRadius: "4px",
      maxHeight: isMobile ? "120px" : "150px",
      overflowY: "auto",
      padding: "10px",
      marginTop: "10px",
      display: isSearchActive ? "block" : "none",
      fontSize: isMobile ? "14px" : "16px",
    },
    searchResultItem: {
      padding: isMobile ? "6px" : "8px",
      cursor: "pointer",
      borderBottom: "1px solid #eee",
      fontSize: isMobile ? "13px" : "14px",
    },
    winnersList: {
      marginBottom: "20px",
    },
    winnerItem: {
      display: "flex",
      alignItems: "center",
      padding: isMobile ? "8px 5px" : "10px",
      gap: isMobile ? "5px" : "10px",
      borderBottom: "1px solid #eee",
      fontSize: isMobile ? "14px" : "16px",
    },
    position: {
      width: isMobile ? "40px" : "50px",
      fontWeight: "bold",
      fontSize: isMobile ? "14px" : "16px",
    },
    winnerName: {
      flex: 1,
      fontSize: isMobile ? "13px" : "14px",
      wordBreak: "break-word",
    },
    editButton: {
      backgroundColor: "#f8f9fa",
      border: "1px solid #ddd",
      borderRadius: "4px",
      padding: isMobile ? "4px 8px" : "5px 10px",
      cursor: "pointer",
      minWidth: isMobile ? "30px" : "auto",
    },
    removeButton: {
      backgroundColor: "#f8f9fa",
      border: "1px solid #ddd",
      borderRadius: "4px",
      padding: isMobile ? "4px 8px" : "5px 10px",
      cursor: "pointer",
      minWidth: isMobile ? "30px" : "auto",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "center",
      gap: isMobile ? "8px" : "10px",
      marginTop: isMobile ? "15px" : "20px",
    },
    button: {
      padding: isMobile ? "6px 12px" : "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: isMobile ? "14px" : "16px",
      minWidth: isMobile ? "70px" : "auto",
    },
    applyButton: {
      backgroundColor: "#4a90e2",
      color: "white",
      border: "none",
    },
    cancelButton: {
      backgroundColor: "#f5f5f5",
      border: "1px solid #ddd",
    },
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      contentStyle={{ 
        maxWidth: "600px", 
        width: isMobile ? "95%" : "80%",
        margin: isMobile ? "10px" : "auto",
        display: "flex",
        justifyContent:"center",
        alignItems:"center"
      }}
      modal
      nested
    >
      <div style={modalStyle.modalContent}>
        <h2 style={{ 
          marginBottom: isMobile ? "15px" : "20px", 
          textAlign: "center",
          fontSize: isMobile ? "18px" : "24px"
        }}>
          Edit Contest Winners
        </h2>

        {/* Search section */}
        <div style={modalStyle.searchContainer}>
          <form onSubmit={handleSearch} style={modalStyle.searchForm}>
            <input
              id="winner-search-input"
              type="text"
              placeholder="Search participants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!isSearchActive}
              style={modalStyle.searchInput}
            />
            <button
              type="submit"
              disabled={!isSearchActive}
              style={modalStyle.searchButton}
            >
              Search
            </button>
          </form>

          {/* Search results */}
          <div style={modalStyle.searchResults}>
            {searchResults.length > 0 ? (
              searchResults.map((user) => (
                <div
                  key={user.id}
                  style={modalStyle.searchResultItem}
                  onClick={() => selectWinner(user.id, user.name)}
                >
                  {user.name} ({user.id})
                </div>
              ))
            ) : (
              <p>No results found</p>
            )}
          </div>
        </div>

        {/* Winners list */}
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading winners...</p>
        ) : (
          <div style={modalStyle.winnersList}>
            <h4 style={{ fontSize: isMobile ? "16px" : "18px" }}>Current Winners</h4>
            {winnersList.length > 0 ? (
              winnersList.map((winner, index) => (
                <div key={index} style={modalStyle.winnerItem}>
                  <div style={modalStyle.position}>#{winner.position}</div>
                  <div style={modalStyle.winnerName}>
                    {winner.name} ({winner.id})
                  </div>
                  <button
                    style={modalStyle.editButton}
                    onClick={() => handleEditClick(index)}
                  >
                    <FontAwesomeIcon
                      icon={faEdit}
                      style={{ fontSize: isMobile ? "11px" : "12px" }}
                    />
                  </button>
                  <button
                    style={modalStyle.removeButton}
                    onClick={() => handleRemoveWinner(index)}
                  >
                    <FontAwesomeIcon 
                      icon={faTrash} 
                      style={{ fontSize: isMobile ? "11px" : "12px", border: "none" }}
                      color="red"
                    />
                  </button>
                </div>
              ))
            ) : (
              <p>No winners set for this contest.</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={modalStyle.buttonGroup}>
          <button
            onClick={onClose}
            style={{ ...modalStyle.button, ...modalStyle.cancelButton }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ ...modalStyle.button, ...modalStyle.applyButton }}
          >
            {isSubmitting ? "Updating..." : "Apply Changes"}
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default EditWinner;